import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, FileText, ShieldCheck, ShieldAlert, Clock } from "lucide-react";

const DOC_TYPES_LORRY = [
  { v: "rc", label: "Vehicle RC" },
  { v: "insurance", label: "Insurance Certificate" },
  { v: "dl", label: "Driving License" },
  { v: "id_proof", label: "ID Proof (Aadhaar/PAN)" },
  { v: "other", label: "Other" },
];
const DOC_TYPES_DRIVER = [
  { v: "dl", label: "Driving License" },
  { v: "id_proof", label: "ID Proof (Aadhaar/PAN)" },
  { v: "insurance", label: "Insurance / Medical" },
  { v: "other", label: "Other" },
];

export default function Documents() {
  const { user, refreshUser } = useAuth();
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("");
  const [busy, setBusy] = useState(false);

  const types = user?.role === "driver" ? DOC_TYPES_DRIVER : DOC_TYPES_LORRY;

  const load = async () => {
    try {
      const { data } = await api.get("/documents/mine");
      setDocs(data.documents);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a file first");
    if (!docType) return toast.error("Choose a document type");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/documents/upload?doc_type=${docType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded. Awaiting admin verification.");
      setFile(null); setDocType("");
      // clear input
      const el = document.getElementById("doc-file");
      if (el) el.value = "";
      await load();
      await refreshUser?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const verified = user?.verified;
  const isProvider = user?.role === "lorry_owner" || user?.role === "driver";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="overline text-[#FF5A00]">DOCUMENT LOCKER</div>
        <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="documents-page">Get verified. Start bidding.</h1>

        {isProvider && (
          <div
            data-testid="verification-banner"
            className={`mt-6 p-4 border rounded-sm flex items-start gap-3 ${
              verified ? "border-emerald-700 bg-emerald-900/20 text-emerald-200" : "border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700]"
            }`}
          >
            {verified ? <ShieldCheck className="w-5 h-5 mt-0.5" /> : <ShieldAlert className="w-5 h-5 mt-0.5" />}
            <div className="text-sm">
              <div className="font-semibold">{verified ? "Verified account" : "Verification pending"}</div>
              <div className="text-xs opacity-80 mt-1">
                {verified
                  ? "Your documents are approved. You can now place bids across the yard."
                  : "Upload at least one document (DL / RC / Insurance). Bidding unlocks after NorthLine Admin approves any one document."}
              </div>
            </div>
          </div>
        )}

        {/* Upload form */}
        <form onSubmit={submit} className="mt-8 grid sm:grid-cols-3 gap-3 items-end bg-[#141414] border border-[#2A2A2A] p-5">
          <div>
            <Label className="overline text-zinc-400">Document type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type-select" className="mt-2 h-11 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-zinc-400">PDF / JPG / PNG (max 8 MB)</Label>
            <Input id="doc-file" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="doc-file-input"
              className="mt-2 h-11 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm file:mr-3 file:h-9 file:px-3 file:bg-[#FF5A00] file:text-black file:font-bold file:border-0 file:rounded-sm" />
          </div>
          <Button type="submit" disabled={busy} data-testid="doc-submit-btn"
            className="h-11 rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">
            <Upload className="w-4 h-4 mr-2" /> {busy ? "Uploading..." : "Upload"}
          </Button>
        </form>

        {/* Docs list */}
        <div className="mt-8 border border-[#2A2A2A]">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Filename</th>
                <th className="text-left p-3">Uploaded</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Review note</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-[#2A2A2A] hover:bg-[#141414]/60">
                  <td className="p-3 uppercase tracking-wider text-xs text-[#FF5A00] font-semibold">{d.doc_type}</td>
                  <td className="p-3 flex items-center gap-2 text-zinc-300">
                    <FileText className="w-4 h-4 text-zinc-500" /> {d.original_filename}
                  </td>
                  <td className="p-3 text-zinc-400 font-mono-ll text-xs">{new Date(d.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-3"><DocStatus s={d.status} /></td>
                  <td className="p-3 text-xs text-zinc-400">{d.review_note || "—"}</td>
                </tr>
              ))}
              {docs.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-zinc-500">No documents uploaded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DocStatus({ s }) {
  if (s === "approved") return <span className="inline-flex items-center gap-1 text-emerald-300 text-xs"><ShieldCheck className="w-3 h-3" /> Approved</span>;
  if (s === "rejected") return <span className="inline-flex items-center gap-1 text-red-400 text-xs"><ShieldAlert className="w-3 h-3" /> Rejected</span>;
  return <span className="inline-flex items-center gap-1 text-[#FFD700] text-xs"><Clock className="w-3 h-3" /> Pending</span>;
}
